// Danh mục + phân nhóm tiện ích P4S — dùng CHUNG giữa:
//  - app/sale/page.tsx (nhóm tiện ích khi HIỂN THỊ trên /sale)
//  - app/(app)/settings/page.tsx (nhóm tiện ích khi CHỌN trong album editor, RoomAlbumEditor)
// Giá trị lưu Firestore LUÔN là chuỗi tiếng Anh gốc (khớp dữ liệu crawl Airbnb hiện có, xem memory
// project_p4s_airbnb_crawl_data) — nhãn tiếng Việt (AMENITY_LABELS_VI) CHỈ để hiển thị, không đổi
// giá trị lưu (đổi giá trị lưu sẽ làm /sale hiển thị khác dữ liệu Airbnb thật).

export const AMENITY_GROUPS: { label: string; icon: string; keywords: string[] }[] = [
  { label: 'An toàn', icon: '🧯', keywords: ['alarm', 'extinguisher', 'first aid', 'window guard'] },
  { label: 'Ra vào & vị trí', icon: '🔑', keywords: ['keypad', 'self check-in', 'private entrance', 'elevator'] },
  { label: 'Bếp & ăn uống', icon: '🍳', keywords: ['kitchen', 'dishes', 'dishwasher', 'microwave', 'refrigerator', 'coffee maker', 'oven', 'stove'] },
  { label: 'Phòng ngủ & tắm', icon: '🛁', keywords: ['bed linens', 'bathtub', 'hair dryer', 'shampoo', 'conditioner', 'body soap', 'shower gel', 'pillow', 'blanket', 'hangers', 'essentials', 'iron'] },
  { label: 'Giặt ủi', icon: '🧺', keywords: ['washer', 'dryer', 'laundry'] },
  { label: 'Tiện nghi chung', icon: '🛋️', keywords: ['air condition', ' tv', 'workspace', 'outdoor furniture', 'hot water', 'wifi', 'long term', 'backyard', 'patio', 'cleaning available'] },
];

export const OTHER_AMENITY_GROUP = { label: 'Khác', icon: '✨' };

/** Gom 1 danh sách tiện ích (chuỗi tự do) vào các nhóm theo keyword substring — không phân biệt
 *  hoa/thường, không khớp nhóm nào → "Khác". Dùng chung cho cả hiển thị (/sale) và chọn (Settings). */
export function groupAmenities(list: string[], opts?: { sortByValue?: boolean }): { label: string; icon: string; items: string[] }[] {
  const buckets = new Map<string, string[]>();
  for (const item of list) {
    const lower = ` ${item.toLowerCase()}`; // đệm khoảng trắng để keyword ' tv' không dính chữ khác
    const group = AMENITY_GROUPS.find(g => g.keywords.some(k => lower.includes(k)));
    const label = group?.label ?? OTHER_AMENITY_GROUP.label;
    const arr = buckets.get(label) ?? [];
    arr.push(item);
    buckets.set(label, arr);
  }
  const allGroups = [...AMENITY_GROUPS, OTHER_AMENITY_GROUP];
  return allGroups.filter(g => buckets.has(g.label)).map(g => {
    const items = buckets.get(g.label)!;
    // Array.sort ổn định → cùng điểm giữ nguyên thứ tự gốc
    return { label: g.label, icon: g.icon, items: opts?.sortByValue ? [...items].sort((a, b) => amenityValue(b) - amenityValue(a)) : items };
  });
}

// "Giá trị" của 1 tiện ích với KHÁCH ĐANG CÂN NHẮC THUÊ (điểm cao = nổi bật, xếp đầu trong nhóm khi hiển thị
// /sale): điểm cộng/trội (view, hồ, hồ bơi, ban công, thang máy, máy giặt…) > tiện nghi cốt lõi (wifi, điều hoà,
// bếp) > đồ cơ bản ai cũng có (xà phòng, móc treo) > thông tin phụ/kỹ thuật (camera, khoá hộp, sưởi, báo cháy).
// Khớp keyword substring không phân biệt hoa/thường; không khớp = điểm trung tính (3).
const AMENITY_VALUE_TIERS: { score: number; keywords: string[] }[] = [
  { score: 9, keywords: ['waterfront', 'lake access', 'lake view', 'view', 'pool', 'hot tub', 'gym', 'sauna', 'bathtub', 'balcony', 'patio', 'backyard', 'garden', 'free parking', 'parking', 'private entrance', 'elevator'] },
  { score: 8, keywords: ['washer', 'full kitchen', 'dedicated workspace', 'workspace', 'long term', 'self check-in', 'keypad', 'dishwasher', 'coffee maker', 'oven'] },
  { score: 7, keywords: ['wifi', 'air condition', 'kitchen', 'tv', 'refrigerator', 'microwave', 'hot water kettle', 'rice maker', 'cooking basics', 'dining table'] },
  { score: 5, keywords: ['hot water', 'stove', 'dishes', 'hair dryer', 'iron', 'luggage', 'cleaning'] },
  { score: 4, keywords: ['bed linens', 'pillows', 'blanket', 'clothing storage', 'closet', 'shampoo', 'conditioner', 'body soap', 'shower gel'] },
  { score: 2, keywords: ['essentials', 'hangers', 'drying rack', 'room-darkening', 'books', 'cleaning products'] },
  { score: 1, keywords: ['alarm', 'extinguisher', 'first aid', 'camera', 'lockbox', 'heating', 'housekeeping available', 'window guard'] },
];

export function amenityValue(item: string): number {
  const lower = item.toLowerCase();
  // Ngoại lệ chuỗi con: "Hair dryer" ≠ máy sấy quần áo; "Cleaning products" ≠ dọn phòng trong kỳ ở
  if (lower.includes('hair dryer')) return 5;
  if (lower.includes('cleaning products')) return 2;
  if (lower.includes('dryer')) return 8;
  // Duyệt từ cao xuống thấp — match đầu tiên thắng ("Hot water kettle" ăn tier 7 trước khi chạm 'hot water' 5)
  const tier = AMENITY_VALUE_TIERS.find(t => t.keywords.some(k => lower.includes(k)));
  return tier?.score ?? 3;
}

// Tổng hợp TOÀN BỘ tiện ích đang thực dùng trên mọi RoomType/Room (Gardenia, 2026-08-21) — nguồn cho
// danh mục tick-chọn nhanh ở RoomAlbumEditor, tránh gõ tay lại tiện ích đã có ở căn khác. Thêm tiện ích
// MỚI chưa có trong danh sách này vẫn được — dùng ô nhập tự do cạnh danh mục.
export const AMENITY_CATALOG = [
  'Air conditioning', 'Backyard', 'Balcony', 'Bathtub', 'Bed linens', 'Body soap',
  'Carbon monoxide alarm', 'Carbon monoxide alarm installed', 'Cleaning available during stay',
  'Coffee maker', 'Coffee maker: espresso machine', 'Conditioner', 'Dedicated workspace',
  'Dishes and silverware', 'Dishwasher', 'Dryer', 'Elevator', 'Essentials',
  'Extra pillows and blankets', 'Fire extinguisher', 'First aid kit', 'Full kitchen',
  'Hair dryer', 'Hangers', 'Hot water', 'Iron', 'Keypad', 'Kitchen', 'Laundry area',
  'Long term stays allowed', 'Microwave', 'Outdoor furniture', 'Oven', 'Patio',
  'Patio or balcony', 'Private entrance', 'Refrigerator', 'Self check-in', 'Shampoo',
  'Shower gel', 'Smoke alarm', 'Smoke alarm installed', 'Stove', 'TV', 'Washer', 'Wifi',
  'Window guards', 'Workspace',
];

// Nhãn tiếng Việt CHỈ để hiển thị cạnh checkbox (dễ chọn hơn cho NV) — KHÔNG phải giá trị lưu.
export const AMENITY_LABELS_VI: Record<string, string> = {
  'Air conditioning': 'Điều hoà',
  'Backyard': 'Sân sau',
  'Balcony': 'Ban công',
  'Bathtub': 'Bồn tắm',
  'Bed linens': 'Ga gối',
  'Body soap': 'Sữa tắm',
  'Carbon monoxide alarm': 'Báo khí CO',
  'Carbon monoxide alarm installed': 'Báo khí CO (đã lắp)',
  'Cleaning available during stay': 'Dọn phòng trong kỳ ở',
  'Coffee maker': 'Máy pha cà phê',
  'Coffee maker: espresso machine': 'Máy pha cà phê espresso',
  'Conditioner': 'Dầu xả',
  'Dedicated workspace': 'Khu làm việc riêng',
  'Dishes and silverware': 'Bát đĩa, dao muỗng',
  'Dishwasher': 'Máy rửa bát',
  'Dryer': 'Máy sấy',
  'Elevator': 'Thang máy',
  'Essentials': 'Đồ dùng cơ bản',
  'Extra pillows and blankets': 'Gối/chăn dự phòng',
  'Fire extinguisher': 'Bình cứu hoả',
  'First aid kit': 'Hộp cứu thương',
  'Full kitchen': 'Bếp đầy đủ',
  'Hair dryer': 'Máy sấy tóc',
  'Hangers': 'Móc treo đồ',
  'Hot water': 'Nước nóng',
  'Iron': 'Bàn là',
  'Keypad': 'Khoá mã số',
  'Kitchen': 'Bếp',
  'Laundry area': 'Khu giặt đồ',
  'Long term stays allowed': 'Cho thuê dài hạn',
  'Microwave': 'Lò vi sóng',
  'Outdoor furniture': 'Bàn ghế ngoài trời',
  'Oven': 'Lò nướng',
  'Patio': 'Sân hiên',
  'Patio or balcony': 'Sân hiên/Ban công',
  'Private entrance': 'Cửa ra vào riêng',
  'Refrigerator': 'Tủ lạnh',
  'Self check-in': 'Tự nhận phòng',
  'Shampoo': 'Dầu gội',
  'Shower gel': 'Sữa tắm (vòi sen)',
  'Smoke alarm': 'Báo cháy',
  'Smoke alarm installed': 'Báo cháy (đã lắp)',
  'Stove': 'Bếp nấu',
  'TV': 'Tivi',
  'Washer': 'Máy giặt',
  'Wifi': 'Wifi',
  'Window guards': 'Chắn cửa sổ (an toàn)',
  'Workspace': 'Khu làm việc',
};
